import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';

import {
  anchoredZoom,
  clampScale,
  clampTranslate,
  nativeMaxScale,
  type Point,
  type Transform,
} from './zoomMath';

/**
 * The image, and every way of moving around it.
 *
 * **Hand-rolled rather than a zoom library**, on the same reasoning that keeps
 * `lucide-react` out of this workspace and left `FolderView` without a confirm
 * dialog: one caller, a small surface, and Article 2 makes a dependency with
 * lock-in an ADR. The reference implementation this viewer is modelled on wraps
 * `react-zoom-pan-pinch`, and its three standing workaround comments — the
 * library measures a 0×0 image on init, its reset restores the corner rather
 * than the centre, and it sets `pointer-events: none` on staged content — are
 * all consequences of that library's model of a synchronously-sized child.
 * Mouse, touch and pen are one pointer-events code path here instead.
 *
 * **The PDF reader is still a different stage, and less different than it was.**
 * `PdfStage` magnifies during a pinch as well now (ADR-0101), because no
 * renderer keeps up with a gesture. What stays its own is the ending: a
 * photograph settles by handing this layer back to the compositor to
 * re-rasterize, where a document is drawn again by pdf.js at the new scale.
 * Do not conclude from the shared `will-change` dance below that one component
 * could serve both.
 *
 * **The arithmetic lives in `zoomMath.ts`**, because none of it is testable
 * through jsdom: nothing lays out, so every rect is 0×0 and `naturalWidth` is
 * always 0. This file is the wiring — listeners, measurement, and the parts of
 * rendering a photograph sharply that are about the compositor rather than
 * about maths.
 *
 * **The one inline style carrying numbers is the transform**, and it is not the
 * raw-px violation it resembles. That rule is about design values — spacing,
 * colour, radii — which must come from tokens; `translate3d(…px) scale(…)` is a
 * computed runtime position with no token equivalent and no design decision in
 * it. Everything else here is a Tailwind class or `token()`.
 */

export interface ZoomStageHandle {
  /** Multiply the current scale, anchored at the centre. For the keyboard. */
  zoomBy: (factor: number) => void;
  reset: () => void;
}

const FIT: Transform = { scale: 1, x: 0, y: 0 };

/** What a double-click zooms to, when it is not zooming back out to fit. */
const DOUBLE_CLICK_SCALE = 2.5;

/**
 * How long after a gesture stops before the layer is handed back for
 * re-rasterizing. Long enough that a flick of the wheel is one gesture, short
 * enough that the image sharpens before a reader has finished looking at it.
 */
const SETTLE_MS = 150;

/** A wheel notch is worth about a fifth of a step; the sign is inverted. */
const WHEEL_SENSITIVITY = 0.0015;

export interface ZoomStageProps {
  src: string;
  alt: string;
  ref?: Ref<ZoomStageHandle>;
}

/**
 * **Returning to fit on a new image is the caller's `key`, not an effect here.**
 * `ImageViewer` keys this component by the file's id, so stepping remounts it
 * and every piece of transform state starts over — which is what a reset is,
 * done by the mechanism React already has for it rather than by a `setState`
 * in an effect body. Keying on the id and not the `src` also means a retry
 * that re-mints the same image keeps the reader's zoom.
 */
export function ZoomStage({ src, alt, ref }: ZoomStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [transform, setTransform] = useState<Transform>(FIT);
  const [maxScale, setMaxScale] = useState(4);
  const [gesturing, setGesturing] = useState(false);

  const settleTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  /** Live pointers by id, in stage-centre coordinates. One pans, two pinch. */
  const pointers = useRef(new Map<number, Point>());

  const zoomed = transform.scale > 1.01;

  /**
   * Measure what the ceiling should be for this image at this stage size.
   *
   * `offsetWidth` and not `getBoundingClientRect().width`: the latter is the
   * *transformed* box, so reading it while zoomed would raise the ceiling every
   * time it was consulted, and the ceiling would climb away from the reader.
   */
  const measure = useCallback(() => {
    const image = imageRef.current;
    if (!image) return;

    setMaxScale(nativeMaxScale(image.naturalWidth, image.offsetWidth, window.devicePixelRatio));
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => {
      observer.disconnect();
    };
  }, [measure]);

  /** Mark the layer as moving, and schedule handing it back. */
  const touchGesture = useCallback(() => {
    setGesturing(true);
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      setGesturing(false);
    }, SETTLE_MS);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(settleTimer.current);
    },
    [],
  );

  /** Constrain a candidate against the stage as it is measured right now. */
  const settle = useCallback((next: Transform): Transform => {
    const stage = stageRef.current;
    const image = imageRef.current;
    if (!stage || !image) return next;

    return clampTranslate(
      next,
      { width: stage.clientWidth, height: stage.clientHeight },
      { width: image.offsetWidth, height: image.offsetHeight },
    );
  }, []);

  const zoomAt = useCallback(
    (factor: number, anchor: Point) => {
      touchGesture();
      setTransform((current) =>
        settle(anchoredZoom(current, clampScale(current.scale * factor, maxScale), anchor)),
      );
    },
    [maxScale, settle, touchGesture],
  );

  useImperativeHandle(
    ref,
    () => ({
      zoomBy(factor: number) {
        zoomAt(factor, { x: 0, y: 0 });
      },
      reset() {
        touchGesture();
        setTransform(FIT);
      },
    }),
    [zoomAt, touchGesture],
  );

  /**
   * The wheel listener is attached by hand, and has to be.
   *
   * React attaches `onWheel` passively, so a `preventDefault` inside it does
   * nothing and the browser zooms the whole page out from under the viewer.
   * Only a listener registered with `{ passive: false }` can refuse that.
   *
   * Re-attached whenever `zoomAt` changes, which is whenever the ceiling moves.
   * Cheap, and the alternative — a handler kept fresh in a ref — writes that
   * ref during render.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const listener = (event: WheelEvent) => {
      event.preventDefault();
      zoomAt(Math.exp(-event.deltaY * WHEEL_SENSITIVITY), stageCentred(stage, event));
    };

    stage.addEventListener('wheel', listener, { passive: false });
    return () => {
      stage.removeEventListener('wheel', listener);
    };
  }, [zoomAt]);

  return (
    <div
      ref={stageRef}
      data-testid="zoom-stage"
      /*
        `touch-none` is what lets a drag pan instead of scrolling the page, and
        `select-none` stops a drag from selecting the chrome behind the image.

        Nothing here may introduce an offscreen buffer — no filter, no
        backdrop-filter, no opacity below 1, no blend mode, on this element or
        any wrapper above the image. Each one forces the compositor to render
        into an intermediate surface and resample it, which is exactly the
        softness the whole viewer exists to avoid.
      */
      className="relative flex min-h-0 flex-1 touch-none items-center justify-center overflow-hidden select-none"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        pointers.current.set(event.pointerId, stageCentred(stageRef.current, event));
        touchGesture();
      }}
      onPointerMove={(event) => {
        const previous = pointers.current.get(event.pointerId);
        if (previous === undefined) return;

        // Snapshot before the update: a pinch needs where *both* pointers were,
        // and one of them is about to be overwritten.
        const before = new Map(pointers.current);
        const current = stageCentred(stageRef.current, event);
        pointers.current.set(event.pointerId, current);
        touchGesture();

        const ids = [...pointers.current.keys()];

        // One pointer pans, and only once there is somewhere to pan to.
        if (ids.length === 1) {
          if (!zoomed) return;
          setTransform((from) =>
            settle({
              scale: from.scale,
              x: from.x + (current.x - previous.x),
              y: from.y + (current.y - previous.y),
            }),
          );
          return;
        }

        // Two pointers pinch. A third and beyond are ignored rather than
        // averaged in: a palm landing on a tablet should not move the picture.
        const [idA, idB] = ids;
        if (idA === undefined || idB === undefined) return;

        const fromA = before.get(idA);
        const fromB = before.get(idB);
        const toA = pointers.current.get(idA);
        const toB = pointers.current.get(idB);
        if (!fromA || !fromB || !toA || !toB) return;

        const spreadBefore = distance(fromA, fromB);
        if (spreadBefore <= 0) return;

        const midBefore = midpoint(fromA, fromB);
        const midAfter = midpoint(toA, toB);

        setTransform((from) => {
          const scaled = anchoredZoom(
            from,
            clampScale(from.scale * (distance(toA, toB) / spreadBefore), maxScale),
            midAfter,
          );

          // Follow the midpoint as well, so two fingers can drag the image at
          // the same time as sizing it — which is how a pinch actually feels.
          return settle({
            scale: scaled.scale,
            x: scaled.x + (midAfter.x - midBefore.x),
            y: scaled.y + (midAfter.y - midBefore.y),
          });
        });
      }}
      onPointerUp={(event) => {
        pointers.current.delete(event.pointerId);
      }}
      onPointerCancel={(event) => {
        pointers.current.delete(event.pointerId);
      }}
      onDoubleClick={(event) => {
        const anchor = stageCentred(stageRef.current, event);
        touchGesture();
        setTransform((current) =>
          settle(
            zoomed ? FIT : anchoredZoom(current, clampScale(DOUBLE_CLICK_SCALE, maxScale), anchor),
          ),
        );
      }}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        fetchPriority="high"
        decoding="async"
        // Native image-drag would otherwise hijack the pan on the first move.
        draggable={false}
        onLoad={measure}
        className="max-h-full max-w-full"
        style={{
          transform: `translate3d(${String(transform.x)}px, ${String(transform.y)}px, 0) scale(${String(transform.scale)})`,
          /*
            Set only while something is moving, and removed once it settles.

            A composited layer is rasterized at the size it had when the layer
            was made; scaling the *layer* magnifies that bitmap, which is why a
            naive `transform: scale()` viewer goes soft the moment it zooms.
            Dropping the hint once the gesture stops is what lets the browser
            re-rasterize at the new scale — the one line that decides whether
            this reads as evidence or as a slideshow.
          */
          willChange: gesturing ? 'transform' : undefined,
          cursor: zoomed ? 'grab' : 'default',
        }}
      />
    </div>
  );
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Client coordinates, re-expressed as offsets from the stage's centre. */
function stageCentred(
  stage: HTMLDivElement | null,
  event: { clientX: number; clientY: number },
): Point {
  if (!stage) return { x: 0, y: 0 };

  const rect = stage.getBoundingClientRect();
  return {
    x: event.clientX - (rect.left + rect.width / 2),
    y: event.clientY - (rect.top + rect.height / 2),
  };
}
